import { appAlert } from '@/lib/appAlert';
import { markNotificationsReadForEntity } from '@/lib/notifications';
import { circleShortName, formatAmount } from '@/lib/format';
import { getErrorMessage } from '@/lib/functions';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import * as Clipboard from 'expo-clipboard';
import * as Linking from 'expo-linking';
import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  Share,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { Button } from '@/components/ui/Button';
import { ChatBubble } from '@/components/ChatBubble';
import { CurrencyHint } from '@/components/CurrencyHint';
import { ConfirmModal } from '@/components/ConfirmModal';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { acceptOffer, isClosedByOther, lineItemTotal, type Offer, type OfferLineItem } from '@/lib/offers';
import { exportOfferPdf } from '@/lib/offerPdf';
import { supabase } from '@/lib/supabase';
import type { Profile as DbProfile } from '@/lib/database.types';
import { ErrorText } from '@/components/ErrorText';

const OFFER_INVITE_BASE = 'https://heirapsia.gr/landing-page/offer-invite.html';

type Profile = { full_name: string | null; nickname?: string | null; avatar_url: string | null; business_name?: string | null; show_business_name?: boolean | null; professional_terms?: string | null; business_id?: string | null };

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export default function OfferDetailScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { id } = useLocalSearchParams<{ id: string }>();
  const { user } = useAuth();
  const { profile } = useProfile();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const [offer, setOffer] = useState<Offer | null>(null);
  const [creator, setCreator] = useState<Profile | null>(null);
  const [senderBusinessName, setSenderBusinessName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Rejection modal state
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);

  // Clone as draft
  const [cloning, setCloning] = useState(false);

  // Withdraw to draft (creator only, while status=sent)
  const [withdrawing, setWithdrawing] = useState(false);

  // Resend email
  const [resending, setResending] = useState(false);


  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const { data: o, error: oErr } = await supabase.rpc('get_offer_for_user', { p_offer_id: id });
      if (oErr) throw oErr;
      if (!o) throw new Error('Offer not found');
      setOffer(o as unknown as Offer);
      // Αν είμαι ο παραλήπτης (όχι ο δημιουργός) — δηλαδή είδα κατευθείαν την
      // προσφορά (π.χ. μέσω push notification), χωρίς να περάσω από την
      // καρτέλα "Δικές μου" — καθάρισε κι εκεί την κόκκινη ένδειξη, ώστε να
      // μη μείνει αναμμένη ενώ την έχω ήδη δει.
      if (user && o?.creator_id !== user.id) {
        void AsyncStorage.setItem('my_requests_offers_last_seen', new Date().toISOString());
      }
      if (o?.creator_id) {
        const { data: profiles } = await supabase.rpc('get_profiles_public', { p_ids: [o.creator_id] });
        setCreator(profiles?.[0] ?? null);
      }
      // Αν ο δημιουργός επέλεξε να στείλει "ως η επιχείρηση", το όνομα της
      // επιχείρησης υπερισχύει του προσωπικού/προεπιλεγμένου ονόματος προφίλ.
      const senderBusinessId = (o as unknown as { sender_business_id?: string | null })?.sender_business_id;
      if (senderBusinessId) {
        const { data: b } = await supabase.from('businesses').select('name').eq('id', senderBusinessId).single();
        setSenderBusinessName((b as { name: string } | null)?.name ?? null);
      } else {
        setSenderBusinessName(null);
      }
    } catch (e) {
      setError(getErrorMessage(e, t('offersScreens.errorLoadFailed')));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useFocusEffect(useCallback(() => {
    void load();
    if (id) void markNotificationsReadForEntity(id);
    if (!id) return;
    const channel = supabase
      .channel(`offer-${id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'offers',
        filter: `id=eq.${id}`,
      }, () => { void load(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [load, id]));

  async function onAccept() {
    if (!offer) return;
    setAccepting(true);
    setError(null);
    try {
      const agreementId = await acceptOffer(offer.id);
      router.replace({ pathname: '/(app)/agreements/[id]', params: { id: agreementId } });
    } catch (e) {
      setError(getErrorMessage(e, t('offersScreens.errorAcceptFailed')));
      setAccepting(false);
    }
  }

  async function onDecline() {
    if (!offer) return;
    setDeclining(true);
    setError(null);
    try {
      const { error: err } = await supabase.rpc('decline_offer', {
        p_offer_id: offer.id,
        p_reason: declineReason.trim() || undefined,
      });
      if (err) throw err;
      setShowDeclineModal(false);
      setDeclineReason('');
      router.replace('/(app)/offers');
    } catch (e) {
      setError(getErrorMessage(e, t('offersScreens.errorDeclineFailed')));
    } finally {
      setDeclining(false);
    }
  }

  // Σκόπιμα ΔΕΝ βάζουμε τον κωδικό μέσα στο link — αν τον βάλαμε, θα ήταν
  // άχρηστος ως φραγμός μόλις κάποιος προωθήσει απλά το link (π.χ. σε group
  // chat), αφού θα άνοιγε αυτόματα για τον καθένα. Ο κωδικός δίνεται ξεχωριστά
  // στο κείμενο κοινοποίησης, ώστε να χρειάζεται να τον πληκτρολογήσει κάποιος.
  function offerUrl() {
    return `${OFFER_INVITE_BASE}?offer=${offer!.id}`;
  }

  function shareText() {
    const codeLine = offer!.invite_code ? t('offersScreens.shareCodeLine', { code: offer!.invite_code }) : '';
    return `${t('offersScreens.shareOfferText', { title: offer!.title })}${codeLine}\n${offerUrl()}`;
  }

  async function onShareGeneric() {
    if (!offer) return;
    try {
      await Share.share({ message: shareText(), url: offerUrl() });
    } catch { /* user cancelled */ }
  }

  async function onShareViber() {
    if (!offer) return;
    const encoded = encodeURIComponent(shareText());
    await Linking.openURL(`viber://forward?text=${encoded}`).catch(() =>
      Linking.openURL(`https://www.viber.com/`)
    );
  }

  async function onShareWhatsApp() {
    if (!offer) return;
    const encoded = encodeURIComponent(shareText());
    await Linking.openURL(`whatsapp://send?text=${encoded}`).catch(() =>
      Linking.openURL(`https://wa.me/?text=${encoded}`)
    );
  }

  async function onShareSms() {
    if (!offer) return;
    const encoded = encodeURIComponent(shareText());
    await Linking.openURL(`sms:?body=${encoded}`).catch(() => {});
  }

  async function onCopyLink() {
    if (!offer) return;
    await Clipboard.setStringAsync(offerUrl());
    appAlert(t('offers.link_copied_title'), t('offers.link_copied_body'));
  }

  async function onResendEmail() {
    if (!offer) return;
    setResending(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/resend-offer-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ offer_id: offer.id }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? t('offersScreens.errorResendFailed'));
      }
      appAlert(t('offersScreens.resentTitle'), t('offersScreens.resentBody', { email: offer.recipient_email }));
    } catch (e) {
      setError(getErrorMessage(e, t('offersScreens.errorResendEmailFailed')));
    } finally {
      setResending(false);
    }
  }

  const [printing, setPrinting] = useState(false);
  async function onPrintOffer() {
    if (!offer || !profile) return;
    setPrinting(true);
    try {
      await exportOfferPdf(
        {
          title: offer.title,
          description: offer.description,
          cost: offer.cost,
          delivery_days: offer.delivery_days,
          offer_kind: offer.offer_kind,
          line_items: offer.line_items,
          created_at: offer.created_at,
        },
        offer.recipient_name?.trim() || t('common.user'),
        offer.include_professional_terms
          ? (profile as DbProfile & { professional_terms?: string | null; business_logo_url?: string | null; business_name?: string | null; show_business_name?: boolean | null })
          : { ...profile, professional_terms: null },
      );
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('offersScreens.printFailed')));
    } finally {
      setPrinting(false);
    }
  }

  async function onWithdrawToDraft() {
    if (!offer) return;
    appAlert(
      t('offers.withdraw_title'),
      t('offers.withdraw_message'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('offers.withdraw_confirm'),
          style: 'destructive',
          onPress: async () => {
            setWithdrawing(true);
            setError(null);
            try {
              const { error: err } = await supabase.rpc('withdraw_offer_to_draft', { p_offer_id: offer.id });
              if (err) throw err;
              await load();
            } catch (e) {
              setError(getErrorMessage(e, t('offersScreens.errorWithdrawFailed')));
            } finally {
              setWithdrawing(false);
            }
          },
        },
      ],
    );
  }

  async function onCloneAsDraft() {
    if (!offer) return;
    setCloning(true);
    setError(null);
    try {
      const { data: newId, error: err } = await supabase.rpc('clone_offer_as_draft', {
        p_offer_id: offer.id,
      });
      if (err) throw err;
      router.push({ pathname: '/(app)/offers/[id]', params: { id: newId } });
    } catch (e) {
      setError(getErrorMessage(e, t('offersScreens.errorCloneFailed')));
    } finally {
      setCloning(false);
    }
  }

  if (loading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!offer) {
    return (
      <View style={styles.loader}>
        <Text style={styles.notFound}>{t('offers.notFound')}</Text>
        <Button
          title={t('common.back')}
          variant="secondary"
          onPress={() => (router.canGoBack() ? router.back() : router.replace('/(app)/offers'))}
        />
      </View>
    );
  }

  const days = daysUntil(offer.expires_at);
  const isExpired = offer.status === 'expired' || (days !== null && days <= 0);
  const isCreator = !!user && offer.creator_id === user.id;
  const canAccept = !isCreator && offer.status === 'sent' && !isExpired;
  const canDecline = !isCreator && offer.status === 'sent' && !isExpired;
  const canShare = false; // share buttons moved to action section
  const creatorPersonalName = creator?.full_name?.trim() ? circleShortName(creator.full_name, creator.nickname) : t('common.user');
  const creatorName = senderBusinessName
    ? senderBusinessName
    : creator?.show_business_name && creator.business_name?.trim()
    ? (creator.business_id ? `${creator.business_name.trim()} — ${creatorPersonalName}` : creator.business_name.trim())
    : creatorPersonalName;

  return (
    <View style={styles.flex}>
      <ScrollView
        contentContainerStyle={[
          styles.content,
          { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
        ]}
      >
        <View style={styles.topBar}>
          <Pressable
            onPress={() => (router.canGoBack() ? router.back() : router.replace('/(app)/offers'))}
            hitSlop={12}
            accessibilityRole="button"
          >
            <Text style={styles.back}>{t('common.back')}</Text>
          </Pressable>
        </View>

        <View style={styles.header}>
          <View style={styles.headerRow}>
            <Ionicons name="briefcase-outline" size={24} color={colors.primary} />
            <Text style={styles.title}>{offer.title}</Text>
          </View>
          {days !== null && !isExpired && (
            <View style={[styles.expiryBadge, days <= 3 && styles.expiryBadgeUrgent]}>
              <Ionicons name="time-outline" size={14} color={days <= 3 ? '#F87171' : colors.textMuted} />
              <Text style={[styles.expiryText, days <= 3 && styles.expiryTextUrgent]}>
                {days === 1 ? t('offers.expiresIn_one') : t('offers.expiresIn_other', { count: days })}
              </Text>
            </View>
          )}
          {isExpired && (
            <View style={styles.expiredBadge}>
              <Text style={styles.expiredText}>{t('offers.expired')}</Text>
            </View>
          )}
          {offer.status === 'declined' && (
            <View style={styles.declinedBadge}>
              <Text style={styles.declinedText}>
                {isClosedByOther(offer) ? t('offers.closedByOther') : t('offers.declined')}
              </Text>
            </View>
          )}
        </View>

        <View style={styles.card}>
          <Text style={styles.fromLabel}>{t('offers.from')}</Text>
          <Text style={styles.fromName}>{creatorName}</Text>
        </View>

        <View style={styles.card}>
          {offer.description ? (
            <>
              <Text style={styles.sectionTitle}>{t('offers.section_description')}</Text>
              <Text style={styles.description}>{offer.description}</Text>
            </>
          ) : (
            <Text style={styles.noDesc}>{t('offers.noDescription')}</Text>
          )}
        </View>

        {(offer as Offer & { offer_kind?: string }).offer_kind === 'goods' ? (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('offersScreens.goodsSectionTitle')}</Text>
            {((offer as Offer & { line_items?: OfferLineItem[] | null }).line_items ?? []).map((it, i) => (
              <View key={i} style={styles.detailRow}>
                <Ionicons name="cube-outline" size={18} color={colors.primary} />
                <Text style={styles.detailLabel} numberOfLines={1}>{it.description} ×{it.quantity}</Text>
                <Text style={styles.detailValue}>{lineItemTotal(it).toFixed(2)} €</Text>
              </View>
            ))}
            {offer.cost != null && (
              <View style={[styles.detailRow, { marginTop: 4 }]}>
                <Ionicons name="cash-outline" size={18} color={colors.primary} />
                <Text style={styles.detailLabel}>{t('offersScreens.finalTotalLabel')}</Text>
                <Text style={styles.detailValue}>{formatAmount(offer.cost, offer.currency)}</Text>
              </View>
            )}
          </View>
        ) : (offer.cost != null || offer.delivery_days != null) && (
          <View style={styles.card}>
            {offer.cost != null && (
              <>
                <View style={styles.detailRow}>
                  <Ionicons name="cash-outline" size={18} color={colors.primary} />
                  <Text style={styles.detailLabel}>{t('offers.field_cost_label')}</Text>
                  <Text style={styles.detailValue}>{formatAmount(offer.cost, offer.currency)}</Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <CurrencyHint amount={offer.cost} currency={offer.currency} />
                </View>
              </>
            )}
            {offer.delivery_days != null && (
              <View style={styles.detailRow}>
                <Ionicons name="time-outline" size={18} color={colors.primary} />
                <Text style={styles.detailLabel}>{t('offers.field_delivery_label')}</Text>
                <Text style={styles.detailValue}>{offer.delivery_days} {t('offers.days_suffix')}</Text>
              </View>
            )}
          </View>
        )}

        {/* Όροι & Προϋποθέσεις επιχείρησης — ώστε ο παραλήπτης να τους διαβάσει πριν αποδεχτεί */}
        {offer.include_professional_terms && !!creator?.professional_terms?.trim() && (
          <View style={styles.card}>
            <Pressable
              style={styles.termsHeader}
              onPress={() => setTermsExpanded((v) => !v)}
              accessibilityRole="button"
            >
              <Ionicons name="document-text-outline" size={16} color={colors.primary} />
              <Text style={[styles.sectionTitle, { flex: 1 }]}>{t('offers.section_terms')}</Text>
              <Ionicons name={termsExpanded ? 'chevron-up' : 'chevron-down'} size={16} color={colors.textMuted} />
            </Pressable>
            {termsExpanded && (
              <Text style={styles.description}>{creator.professional_terms}</Text>
            )}
          </View>
        )}

        {/* Decline reason (visible to creator after rejection) */}
        {isCreator && offer.status === 'declined' && !!(offer as Offer & { decline_reason?: string }).decline_reason && (
          <View style={[styles.card, styles.declineReasonCard]}>
            <Text style={styles.sectionTitle}>{t('offers.section_declineReason')}</Text>
            <Text style={styles.description}>{(offer as Offer & { decline_reason?: string }).decline_reason}</Text>
          </View>
        )}

        <ErrorText>{error}</ErrorText>

        {/* Παραλήπτης: αποδοχή & απόρριψη */}
        {canAccept && (
          <View style={styles.actions}>
            <View style={styles.infoBox}>
              <Ionicons name="information-circle-outline" size={16} color={colors.primary} />
              <Text style={styles.infoText}>{t('offers.acceptanceInfo')}</Text>
            </View>
            <Button
              title={t('offers.action_accept')}
              icon="checkmark-circle-outline"
              onPress={() => setShowConfirm(true)}
              loading={accepting}
            />
          </View>
        )}
        {canDecline && (
          <Button
            title={t('offers.decline')}
            variant="secondary"
            icon="close-circle-outline"
            onPress={() => setShowDeclineModal(true)}
          />
        )}

        {/* Δημιουργός: αναμονή αποδοχής */}
        {isCreator && offer.status === 'sent' && !isExpired && (
          <View style={styles.actions}>
            <View style={[styles.notice, styles.noticeWaiting]}>
              <Ionicons name="hourglass-outline" size={20} color={colors.textMuted} />
              <Text style={styles.noticeText}>{t('offers.waitingForAcceptance')}</Text>
            </View>

            {/* ── Κοινοποίηση ── */}
            <View style={styles.shareSection}>
              <Text style={styles.shareSectionLabel}>{t('offers.shareLink_label')}</Text>
              <View style={styles.shareRow}>
                <Pressable style={styles.shareChip} onPress={onShareViber} accessibilityRole="button">
                  <Text style={styles.shareChipEmoji}>💬</Text>
                  <Text style={styles.shareChipText}>Viber</Text>
                </Pressable>
                <Pressable style={styles.shareChip} onPress={onShareWhatsApp} accessibilityRole="button">
                  <Text style={styles.shareChipEmoji}>📱</Text>
                  <Text style={styles.shareChipText}>WhatsApp</Text>
                </Pressable>
                <Pressable style={styles.shareChip} onPress={onShareSms} accessibilityRole="button">
                  <Text style={styles.shareChipEmoji}>✉️</Text>
                  <Text style={styles.shareChipText}>SMS</Text>
                </Pressable>
                <Pressable style={styles.shareChip} onPress={onCopyLink} accessibilityRole="button">
                  <Ionicons name="copy-outline" size={16} color={colors.primary} />
                  <Text style={styles.shareChipText}>{t('offers.copy_chip')}</Text>
                </Pressable>
                <Pressable style={styles.shareChip} onPress={onShareGeneric} accessibilityRole="button">
                  <Ionicons name="share-outline" size={16} color={colors.primary} />
                  <Text style={styles.shareChipText}>{t('offers.other_chip')}</Text>
                </Pressable>
              </View>
            </View>

            {/* ── Επαναποστολή email ── */}
            {!!offer.recipient_email && (
              <Button
                title={t('offersScreens.resendEmailButton', { email: offer.recipient_email })}
                variant="secondary"
                icon="mail-outline"
                onPress={onResendEmail}
                loading={resending}
              />
            )}

            <Button
              title={t('offers.action_withdrawDraft')}
              variant="secondary"
              icon="arrow-undo-outline"
              onPress={onWithdrawToDraft}
              loading={withdrawing}
            />
          </View>
        )}

        {/* Δημιουργός: η προσφορά έγινε δεκτή */}
        {isCreator && offer.status === 'accepted' && offer.agreement_id && (
          <View style={styles.actions}>
            <View style={[styles.infoBox, styles.infoBoxSuccess]}>
              <Ionicons name="checkmark-circle-outline" size={16} color={colors.success} />
              <Text style={[styles.infoText, { color: colors.success }]}>{t('offers.acceptedSuccess')}</Text>
            </View>
            <Button
              title={t('offers.action_viewAgreement')}
              icon="document-text-outline"
              onPress={() => router.push({ pathname: '/(app)/agreements/[id]', params: { id: offer.agreement_id! } })}
            />
          </View>
        )}

        {/* Παραλήπτης: η προσφορά έγινε δεκτή */}
        {!isCreator && offer.status === 'accepted' && (
          <View style={styles.notice}>
            <Text style={styles.noticeText}>{t('offers.acceptedWaiting')}</Text>
            {offer.agreement_id && (
              <Button
                title={t('offers.action_viewAgreement')}
                icon="document-text-outline"
                onPress={() => router.push({ pathname: '/(app)/agreements/[id]', params: { id: offer.agreement_id! } })}
              />
            )}
          </View>
        )}

        {/* Δημιουργός: επανάχρηση ως πρόχειρο μετά από απόρριψη/λήξη */}
        {isCreator && (offer.status === 'declined' || offer.status === 'expired') && (
          <Button
            title={t('offers.action_reuseAsDraft')}
            variant="secondary"
            icon="copy-outline"
            onPress={onCloneAsDraft}
            loading={cloning}
          />
        )}

        {/* Δημιουργός: πάντα διαθέσιμο PDF προσφοράς για επανεκτύπωση */}
        {isCreator && offer.status !== 'draft' && (
          <Button
            title={t('offersScreens.printAgainButton')}
            variant="secondary"
            icon="print-outline"
            onPress={onPrintOffer}
            loading={printing}
          />
        )}
      </ScrollView>

      {offer.status === 'sent' && (
        <ChatBubble otherUserId={isCreator ? null : offer.creator_id} label={creatorName} />
      )}

      <ConfirmModal
        visible={showConfirm}
        title={t('offers.confirmAccept_title')}
        message={t('offers.confirmAccept_message')}
        confirmLabel={t('offers.confirmAccept_label')}
        onConfirm={() => { setShowConfirm(false); void onAccept(); }}
        onCancel={() => setShowConfirm(false)}
        loading={accepting}
      />

      {/* Decline Sheet (χωρίς native Modal — Android δεν κληρονομεί keyboard-resize μέσα σε Dialog windows) */}
      {showDeclineModal && (
      <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('offers.decline_title')}</Text>
            <Text style={styles.modalSubtitle}>{t('offers.decline_subtitle')}</Text>
            <TextInput
              style={styles.reasonInput}
              placeholder={t('offersScreens.declineReasonPlaceholder')}
              placeholderTextColor={colors.textMuted}
              value={declineReason}
              onChangeText={setDeclineReason}
              multiline
              numberOfLines={3}
              maxLength={300}
            />
            <View style={styles.modalActions}>
              <Button
                title={t('common.cancel')}
                variant="secondary"
                onPress={() => { setShowDeclineModal(false); setDeclineReason(''); }}
              />
              <Button
                title={t('offers.decline')}
                onPress={onDecline}
                loading={declining}
              />
            </View>
          </View>
        </View>
        </KeyboardAvoidingView>
      </View>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    loader: {
      flex: 1, alignItems: 'center', justifyContent: 'center',
      gap: spacing.md, backgroundColor: colors.background,
    },
    notFound: { color: colors.text, fontSize: fontSize.md },
    content: { paddingHorizontal: spacing.lg, gap: spacing.lg },
    topBar: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600' },
    shareBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    shareBtnText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    header: { gap: spacing.sm },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    title: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700', flex: 1 },
    expiryBadge: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      alignSelf: 'flex-start',
      backgroundColor: colors.surface, borderRadius: radius.full,
      paddingHorizontal: spacing.sm, paddingVertical: 4,
      borderWidth: 1, borderColor: colors.border,
    },
    expiryBadgeUrgent: { borderColor: '#F87171', backgroundColor: 'rgba(248,113,113,0.08)' },
    expiryText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    expiryTextUrgent: { color: '#F87171' },
    expiredBadge: {
      alignSelf: 'flex-start',
      backgroundColor: 'rgba(248,113,113,0.12)', borderRadius: radius.full,
      paddingHorizontal: spacing.sm, paddingVertical: 4,
      borderWidth: 1, borderColor: 'rgba(248,113,113,0.3)',
    },
    expiredText: { color: '#F87171', fontSize: fontSize.sm, fontWeight: '700' },
    declinedBadge: {
      alignSelf: 'flex-start',
      backgroundColor: 'rgba(248,113,113,0.12)', borderRadius: radius.full,
      paddingHorizontal: spacing.sm, paddingVertical: 4,
      borderWidth: 1, borderColor: 'rgba(248,113,113,0.3)',
    },
    declinedText: { color: '#F87171', fontSize: fontSize.sm, fontWeight: '700' },
    card: {
      backgroundColor: colors.surface, borderRadius: radius.lg,
      borderWidth: 1, borderColor: colors.border, padding: spacing.md, gap: spacing.sm,
      ...shadow.card,
    },
    declineReasonCard: {
      borderColor: 'rgba(248,113,113,0.3)',
      backgroundColor: 'rgba(248,113,113,0.06)',
    },
    fromLabel: { color: colors.textMuted, fontSize: fontSize.sm },
    fromName: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    sectionTitle: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    termsHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    description: { color: colors.text, fontSize: fontSize.md, lineHeight: 22 },
    noDesc: { color: colors.textMuted, fontSize: fontSize.md, fontStyle: 'italic' },
    detailRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    detailLabel: { color: colors.textMuted, fontSize: fontSize.md, flex: 1 },
    detailValue: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    error: { color: colors.danger, fontSize: fontSize.sm, textAlign: 'center' },
    actions: { gap: spacing.md },
    infoBox: {
      flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
      backgroundColor: 'rgba(99,102,241,0.08)', borderRadius: radius.md,
      borderWidth: 1, borderColor: 'rgba(99,102,241,0.2)', padding: spacing.md,
    },
    infoBoxSuccess: {
      backgroundColor: 'rgba(74,222,128,0.08)',
      borderColor: 'rgba(74,222,128,0.25)',
    },
    infoText: { flex: 1, color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 20 },
    notice: {
      flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
      backgroundColor: colors.surface, borderRadius: radius.md,
      borderWidth: 1, borderColor: colors.border, padding: spacing.md,
    },
    noticeWaiting: {
      backgroundColor: 'rgba(251,191,36,0.10)',
      borderColor: 'rgba(251,191,36,0.30)',
    },
    noticeText: { flex: 1, color: colors.textMuted, fontSize: fontSize.md, lineHeight: 22 },
    // Decline Modal
    modalOverlay: {
      flex: 1, backgroundColor: 'rgba(0,0,0,0.55)',
      alignItems: 'center', justifyContent: 'center',
      padding: spacing.lg,
    },
    modalCard: {
      width: '100%', backgroundColor: colors.surface,
      borderRadius: radius.xl, padding: spacing.lg,
      gap: spacing.md, borderWidth: 1, borderColor: colors.border,
    },
    modalTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    modalSubtitle: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 20 },
    reasonInput: {
      backgroundColor: colors.background, borderRadius: radius.md,
      borderWidth: 1, borderColor: colors.border,
      color: colors.text, fontSize: fontSize.md,
      padding: spacing.md, minHeight: 80,
      textAlignVertical: 'top',
    },
    modalActions: { flexDirection: 'row', gap: spacing.sm },
    shareSection: { gap: spacing.sm },
    shareSectionLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    shareRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
    shareChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 7,
      paddingHorizontal: spacing.sm,
    },
    shareChipEmoji: { fontSize: 15 },
    shareChipText: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600' },
  });
}
